import { ConversationHeaderBar } from "@circle-react-shared/AiConversation/header/ConversationHeaderBar";
import { CircleAIAppShell } from "@circle-react-uikit/CircleAIAppShell";
import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import { formatDailyBriefTitle } from "@circle-react/components/CircleAi/DailyBriefs/formatDailyBriefTitle";
import { circleAiProjectQueries } from "@circle-react/lib/queries/circleAiProjects";
import { Breadcrumbs } from "@circleco/compass/components/Breadcrumbs";
import { Typography } from "@circleco/compass/components/Typography";
import { Link } from "@tanstack/react-router";
import { useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import {
  type CircleAiSurface,
  useCircleAiStore,
} from "@/react/components/CircleAi/useCircleAiStore";
import { useShellReveal } from "@/react/components/CircleAi/useShellReveal";
import {
  BRIEFS_I18N_ROOT,
  BRIEFS_PATH,
} from "@/react/pages/CircleAi/Briefs/constants";
import { PROJECTS_DETAIL_PATH } from "@/react/pages/CircleAi/Projects/constants";

import { ConversationActionsProvider } from "../ConversationActions/ConversationActionsProvider";
import { ConversationActionsTrigger } from "../ConversationActions/ConversationActionsTrigger";
import { NewConversationActionsMenu } from "../ConversationActions/NewConversationActionsMenu";

interface ConversationHeaderProps {
  surface: CircleAiSurface;
}

export const ConversationHeader = ({ surface }: ConversationHeaderProps) => {
  const isOnboardingShell = useShellReveal() === "hidden";
  const conversationId = useCircleAiStore(s => s.activeConversation.id);
  const conversationTitle = useCircleAiStore(s => s.activeConversation.title);
  const projectId = useCircleAiStore(s => s.activeConversation.projectId);
  const dailyBrief = useCircleAiStore(s => s.activeConversation.dailyBrief);
  const isDailyBriefConversation = useCircleAiStore(
    s => s.activeConversation.dailyBriefConversation,
  );
  const panelState = useCircleAIShellStore(s => s.conversationPanelState);
  const setConversationPanelState = useCircleAIShellStore(
    s => s.setConversationPanelState,
  );
  const { data: project } = useQuery({
    ...circleAiProjectQueries.detail(projectId ?? ""),
    enabled: Boolean(projectId),
  });
  const hasProject = projectId && project;

  const title = dailyBrief
    ? formatDailyBriefTitle(dailyBrief)
    : conversationTitle || t("circle_v4.conversation_panel.title");
  const isPanel = surface === "panel";
  // Shown at every width: the same menu already renders in the docked panel's
  // recent-chats drawer, so gating it on fullscreen only hides Copy link.
  const shouldShowActionsMenu = Boolean(conversationId);
  const shouldShowFullscreenToggle = isPanel;
  const shouldShowClose = isPanel && panelState === "default";

  const handleProjectBreadcrumbClick = (): void => {
    if (isPanel && panelState === "fullscreen") {
      setConversationPanelState("default");
    }
  };

  const parentCrumb = (hasProject || dailyBrief) && {
    label: hasProject ? hasProject.name : t(`${BRIEFS_I18N_ROOT}.title`),
    renderLink: hasProject ? (
      <Link
        to={PROJECTS_DETAIL_PATH}
        params={{ projectId: hasProject.uuid }}
        onClick={handleProjectBreadcrumbClick}
      />
    ) : (
      <Link to={BRIEFS_PATH} onClick={handleProjectBreadcrumbClick} />
    ),
  };

  if (isOnboardingShell) return null;

  return (
    <ConversationHeaderBar data-testid="circle-ai-conversation-header">
      <div className="min-w-0 overflow-hidden">
        {parentCrumb && (
          <Breadcrumbs items={[parentCrumb, { label: title }]} size="sm" />
        )}
        {!parentCrumb && (
          <Typography variant="heading-sm" color="primary" truncate>
            {title}
          </Typography>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!conversationId && <NewConversationActionsMenu />}
        {shouldShowActionsMenu && conversationId && (
          <ConversationActionsProvider>
            <ConversationActionsTrigger
              conversation={{
                uuid: conversationId,
                title: conversationTitle,
                projectUuid: projectId,
                isDailyBriefConversation: Boolean(isDailyBriefConversation),
              }}
            />
          </ConversationActionsProvider>
        )}
        {shouldShowFullscreenToggle && (
          <CircleAIAppShell.ConversationPanelFullscreenButton />
        )}
        {shouldShowClose && <CircleAIAppShell.ConversationPanelCloseButton />}
      </div>
    </ConversationHeaderBar>
  );
};
